import React, { createContext, useContext, useState, useRef, ReactNode } from 'react';
import { ApiService } from '../services/api';

interface ImportProgress {
  jobId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  total: number;
  processed: number;
  inserted: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: Array<{ row: number; error: string; data?: string }>;
  startedAt: string;
  completedAt?: string;
}

interface ImportState {
  isImporting: boolean;
  isMinimized: boolean;
  progress: ImportProgress | null;
  type: 'company' | 'people' | null;
  file: File | null;
  preview: any[];
  error: string | null;
  onComplete?: () => void;
}

interface ImportContextType {
  state: ImportState;
  startImport: (file: File, type: 'company' | 'people', onComplete?: () => void) => Promise<void>;
  minimize: () => void;
  expand: () => void;
  close: () => void;
  clearError: () => void;
}

const ImportContext = createContext<ImportContextType | undefined>(undefined);

export const useImport = () => {
  const context = useContext(ImportContext);
  if (!context) {
    throw new Error('useImport must be used within ImportProvider');
  }
  return context;
};

interface ImportProviderProps {
  children: ReactNode;
}

export const ImportProvider: React.FC<ImportProviderProps> = ({ children }) => {
  const [state, setState] = useState<ImportState>({
    isImporting: false,
    isMinimized: false,
    progress: null,
    type: null,
    file: null,
    preview: [],
    error: null,
  });

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const pollProgress = async (jobId: string) => {
    try {
      const progressData = await ApiService.getImportProgress(jobId);
      
      setState(prev => {
        // Only update if progress actually changed
        if (prev.progress && 
            prev.progress.processed === progressData.processed &&
            prev.progress.status === progressData.status) {
          return prev;
        }
        return { ...prev, progress: progressData };
      });

      if (progressData.status === 'completed' || progressData.status === 'failed') {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        
        // Keep the indicator visible but stop showing as "importing"
        setState(prev => ({ ...prev, isImporting: false }));

        if (progressData.status === 'completed' && state.onComplete) {
          state.onComplete();
        }
      }
    } catch (err: any) {
      console.error('Error polling progress:', err);
      setState(prev => ({ 
        ...prev, 
        error: err.message || 'Failed to get import progress' 
      }));
    }
  };

  const startImport = async (file: File, type: 'company' | 'people', onComplete?: () => void) => {
    // Check if already importing
    if (state.isImporting) {
      setState(prev => ({ 
        ...prev, 
        error: 'An import is already in progress. Please wait for it to complete.' 
      }));
      return;
    }

    setState({
      isImporting: true,
      isMinimized: false,
      progress: null,
      type,
      file,
      preview: [],
      error: null,
      onComplete,
    });

    try {
      const jobId = await ApiService.uploadImportFile(file, type, 'upsert');
      
      setState(prev => ({
        ...prev,
        progress: {
          jobId,
          status: 'queued',
          total: 0,
          processed: 0,
          inserted: 0,
          updated: 0,
          skipped: 0,
          failed: 0,
          errors: [],
          startedAt: new Date().toISOString(),
        },
      }));

      // Start polling for progress
      pollIntervalRef.current = setInterval(() => {
        pollProgress(jobId);
      }, 2000);

    } catch (err: any) {
      setState(prev => ({ 
        ...prev, 
        error: err.message || 'Failed to start import',
        isImporting: false 
      }));
    }
  };

  const minimize = () => {
    setState(prev => {
      if (prev.isMinimized) return prev; // Already minimized, prevent re-render
      return { ...prev, isMinimized: true };
    });
  };

  const expand = () => {
    setState(prev => {
      if (!prev.isMinimized) return prev; // Already expanded, prevent re-render
      return { ...prev, isMinimized: false };
    });
  };

  const close = () => {
    // Only allow closing if import is complete or failed
    if (state.progress && (state.progress.status === 'processing' || state.progress.status === 'queued')) {
      return; // Don't close while processing
    }

    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    setState({
      isImporting: false,
      isMinimized: false,
      progress: null,
      type: null,
      file: null,
      preview: [],
      error: null,
    });
  };

  const clearError = () => {
    setState(prev => {
      if (!prev.error) return prev; // No error, prevent re-render
      return { ...prev, error: null };
    });
  };

  return (
    <ImportContext.Provider
      value={{
        state,
        startImport,
        minimize,
        expand,
        close,
        clearError,
      }}
    >
      {children}
    </ImportContext.Provider>
  );
};

